import React from "react";
import {
    Box, Card, CardContent, Typography, Chip,
    List, ListItem, ListItemText, Divider, Button, Alert,
} from "@mui/material";

function exportConsentHistoryCSV(consentHistory = []) {
    const header = "Versión,Estado,Fecha\n";
    const rows = consentHistory.map(h => `${h.version},${h.status},${h.date}`).join("\n");
    const csv = header + rows;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "consentimientos.csv";
    a.click();
    URL.revokeObjectURL(url);
}

function AccountSecurityBlock({ twoFAEnabled, lastPasswordChange }) {
    return (
        <Card sx={{ mb: 4 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>🔐 Seguridad de la Cuenta</Typography>
                {!twoFAEnabled && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
            ¡Activa el 2FA para mayor seguridad y cumplimiento!
                    </Alert>
                )}
                <Box display="flex" alignItems="center" gap={2} mt={1}>
                    <Typography>2FA:</Typography>
                    <Chip
                        label={twoFAEnabled ? "Activado" : "Desactivado"}
                        color={twoFAEnabled ? "success" : "default"}
                    />
                </Box>
                <Typography sx={{ mt: 1 }}>
          Último cambio de contraseña: <b>{lastPasswordChange || "No disponible"}</b>
                </Typography>
                <Button variant="outlined" sx={{ mt: 2 }}>Gestionar dispositivos</Button>
            </CardContent>
        </Card>
    );
}

function ActivityAlertsBlock({ loginHistory, devices, securityAlerts }) {
    return (
        <Card sx={{ mb: 4 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>📊 Actividad y Alertas</Typography>

                <Divider sx={{ my: 2 }} textAlign="left">Historial de Login</Divider>
                <List dense>
                    {loginHistory.length === 0 ? <Typography color="text.secondary">Sin registros.</Typography> :
                        loginHistory.map((item, idx) => (
                            <ListItem key={idx}>
                                <ListItemText
                                    primary={`${item.date} - ${item.device}`}
                                    secondary={`IP: ${item.ip} | Ubicación: ${item.location}`}
                                />
                            </ListItem>
                        ))
                    }
                </List>

                <Divider sx={{ my: 2 }} textAlign="left">Dispositivos Conectados</Divider>
                <List dense>
                    {devices.length === 0 ? <Typography color="text.secondary">Sin dispositivos activos.</Typography> :
                        devices.map((d, idx) => (
                            <ListItem key={idx}>
                                <ListItemText
                                    primary={d.name}
                                    secondary={`Última actividad: ${d.lastActive}`}
                                />
                            </ListItem>
                        ))
                    }
                </List>

                <Divider sx={{ my: 2 }} textAlign="left">Alertas de Seguridad</Divider>
                <List dense>
                    {securityAlerts.length === 0 ? <Typography color="text.secondary">Sin alertas registradas.</Typography> :
                        securityAlerts.map((a, idx) => (
                            <ListItem key={idx}>
                                <ListItemText
                                    primary={a.type}
                                    secondary={`${a.date} - ${a.resolved ? "Resuelta" : "Pendiente"}`}
                                />
                            </ListItem>
                        ))
                    }
                </List>
            </CardContent>
        </Card>
    );
}

function GDPRComplianceBlock({ consentHistory, dataProcessors }) {
    return (
        <Card sx={{ mb: 4 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>⚖️ GDPR y Cumplimiento</Typography>

                <Divider sx={{ my: 2 }} textAlign="left">Historial de Consentimientos</Divider>
                <List dense>
                    {consentHistory.length === 0 ? <Typography color="text.secondary">Sin consentimientos registrados.</Typography> :
                        consentHistory.map((c, idx) => (
                            <ListItem key={idx}>
                                <ListItemText
                                    primary={`Versión ${c.version}`}
                                    secondary={`${c.date} - ${c.status}`}
                                />
                            </ListItem>
                        ))
                    }
                </List>
                <Button variant="outlined" sx={{ mt: 2 }} onClick={() => exportConsentHistoryCSV(consentHistory)}>
          Exportar consentimientos (CSV)
                </Button>

                <Divider sx={{ my: 2 }} textAlign="left">Procesadores de Datos</Divider>
                <List dense>
                    {dataProcessors.length === 0 ? <Typography color="text.secondary">Sin terceros registrados.</Typography> :
                        dataProcessors.map((p, idx) => (
                            <ListItem key={idx}>
                                <ListItemText
                                    primary={p.name}
                                    secondary={`Finalidad: ${p.purpose} | DPA firmado: ${p.dpa ? "Sí" : "No"}`}
                                />
                            </ListItem>
                        ))
                    }
                </List>
            </CardContent>
        </Card>
    );
}

export default function ComplianceSecuritySection({
    twoFAEnabled = false,
    lastPasswordChange = "-",
    loginHistory = [],
    devices = [],
    securityAlerts = [],
    consentHistory = [],
    dataProcessors = [],
}) {
    return (
        <Box>
            <AccountSecurityBlock
                twoFAEnabled={twoFAEnabled}
                lastPasswordChange={lastPasswordChange}
            />
            <ActivityAlertsBlock
                loginHistory={loginHistory}
                devices={devices}
                securityAlerts={securityAlerts}
            />
            <GDPRComplianceBlock
                consentHistory={consentHistory}
                dataProcessors={dataProcessors}
            />
        </Box>
    );
} 